import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { api } from "@/api";
import LanguageToggle from "@/components/LanguageToggle";
import { OrnateHeartDivider } from "@/components/FloralOrnaments";
import { applyTheme } from "@/themes/contract";
import { getTheme } from "@/themes/registry";
import { Heart } from "lucide-react";

export default function HomePage() {
    const { weddingSlug } = useParams();
    const { lang } = useLang();
    const [wedding, setWedding] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let cancelled = false;
        setStatus("loading");
        setWedding(null);
        api.get(`/w/${weddingSlug}/wedding`)
            .then((r) => {
                if (cancelled) return;
                setWedding(r.data);
                setStatus("ready");
            })
            .catch((e) => {
                if (cancelled) return;
                setStatus(e?.response?.status === 404 ? "missing" : "error");
            });
        return () => {
            cancelled = true;
        };
    }, [weddingSlug]);

    const theme = getTheme(wedding?.theme_id);
    const frameClass = "wd-theme wd-paper-texture min-h-[100dvh] flex items-center justify-center p-6 relative";

    if (status === "missing" || status === "error") {
        return (
            <div
                className={frameClass}
                data-ornament={theme.ornament}
                style={applyTheme(theme)}
            >
                <div className="absolute top-4 right-4"><LanguageToggle /></div>
                <div className="wd-card p-10 max-w-md text-center" data-testid="home-not-found">
                    <p className="wd-heading text-3xl text-[var(--wd-primary)]">404</p>
                    <p className="mt-3 text-[var(--wd-text-muted)] text-sm">
                        {status === "missing"
                            ? (lang === "ar" ? "لم يتم العثور على هذا الزفاف." : "This wedding could not be found.")
                            : (lang === "ar" ? "تعذر تحميل هذا الزفاف." : "This wedding could not be loaded.")}
                    </p>
                </div>
            </div>
        );
    }

    const coupleName = wedding
        ? lang === "ar"
            ? `${wedding.groom_ar} & ${wedding.bride_ar}`
            : `${wedding.groom_en} & ${wedding.bride_en}`
        : lang === "ar"
            ? "عوضي & منة"
            : "Awady & Menna";

    return (
        <div
            className="wd-theme wd-paper-texture min-h-[100dvh] flex items-center justify-center p-6 relative"
            data-ornament={theme.ornament}
            style={applyTheme(theme)}
        >
            <div className="absolute top-4 right-4"><LanguageToggle /></div>

            <div className="wd-card p-10 max-w-md text-center" data-testid="home-card">
                <Heart className="mx-auto text-[var(--wd-rose)]" size={32} fill="currentColor" />
                <h1 className="wd-script text-5xl text-[var(--wd-primary)] mt-4">
                    {coupleName}
                </h1>
                <div className="mt-4 flex justify-center"><OrnateHeartDivider width={160} /></div>
                <p className="mt-4 text-[var(--wd-text-muted)] text-sm" data-testid="home-welcome-text">
                    {lang === "ar"
                        ? `هذه منصة الدعوات الخاصة بحفل زفاف ${coupleName}. يتم إرسال رابط مخصص لكل ضيف.`
                        : `This is the private invitation platform for ${coupleName}'s wedding. A personalized link is sent to each guest.`}
                </p>
                <Link
                    to="/admin"
                    className="wd-btn wd-btn-ghost mt-8 inline-block"
                    data-testid="home-admin-link"
                >
                    {lang === "ar" ? "دخول المسؤول" : "Admin Login"}
                </Link>
            </div>
        </div>
    );
}
